import React, { useEffect, useState } from "react";
import { Radar, Zap, Database } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export default function IntentPage() {
  const { t, language } = useLanguage();
  const [partial, setPartial] = useState("");
  const [latest, setLatest] = useState(null);
  const [warm, setWarm] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function loadWarm() {
    try {
      const r = await api.get("/intent/warm-cache");
      setWarm(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    loadWarm();
  }, []);

  async function predict() {
    if (!partial.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/intent/predict", { partial });
      setLatest(data);
      // Let the prefetch background task land before we re-read warm cache.
      setTimeout(loadWarm, 1200);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="intent-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.intent.eyebrow}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.intent.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.intent.sub}</p>
      </header>

      {error && (
        <div className="mios-card" data-testid="intent-error">
          <div className="mios-card-body text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mios-card" data-testid="intent-input-card">
        <div className="mios-card-header">
          <div className="mios-card-title flex items-center gap-2">
            <Radar className="h-4 w-4 text-slate-400" />
            {t.intent.tryIt}
          </div>
        </div>
        <div className="mios-card-body space-y-3">
          <input
            value={partial}
            onChange={(e) => setPartial(e.target.value)}
            placeholder={t.intent.partialPlaceholder}
            data-testid="intent-input"
            className="mios-input"
            onKeyDown={(e) => {
              if (e.key === "Enter") predict();
            }}
          />
          <button
            type="button"
            onClick={predict}
            disabled={busy || !partial.trim()}
            data-testid="intent-predict-btn"
            className="mios-btn mios-btn-primary"
          >
            <Zap className="h-4 w-4" />
            {busy ? t.intent.predicting : t.intent.predict}
          </button>

          {latest && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/40 p-3">
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.intent.hypotheses}
              </div>
              {(latest.hypotheses || []).length === 0 ? (
                <div className="mt-2 text-sm text-slate-500">{t.intent.noHypotheses}</div>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {latest.hypotheses.map((h, i) => (
                    <li
                      key={i}
                      data-testid={`intent-hypothesis-${i}`}
                      className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium text-slate-900 mono">{h.intent}</div>
                        <div className="text-[11px] text-slate-500">
                          {(h.prefetch_keys || []).join(" · ")}
                        </div>
                      </div>
                      <span className="mios-pill mios-pill-normal mono">
                        {Math.round((h.confidence || 0) * 100)}%
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mios-card" data-testid="intent-warm-cache">
        <div className="mios-card-header">
          <div className="mios-card-title flex items-center gap-2">
            <Database className="h-4 w-4 text-slate-400" />
            {t.intent.warmCache}
          </div>
          <span className="text-[11px] text-slate-400">{warm.length}</span>
        </div>
        <div className="mios-card-body">
          {warm.length === 0 ? (
            <div className="text-sm text-slate-400">{t.intent.warmEmpty}</div>
          ) : (
            <ul className="space-y-2">
              {warm.map((row) => (
                <li
                  key={row.id}
                  className="rounded-md border border-slate-200 p-3 text-sm"
                  data-testid={`warm-cache-row-${row.id}`}
                >
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span className="mono">{(row.keys || []).join(" · ")}</span>
                    <span>{new Date(row.created_at).toLocaleString(language)}</span>
                  </div>
                  <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">
                    {JSON.stringify(row.snapshot || {}, null, 2)}
                  </pre>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
